import { Injectable, signal, inject, untracked } from '@angular/core';
import {GamePlay} from "../model/game-play";
import {GameInfo} from "../model/game";
import {ODK} from "../model/odk";
import {GameStatus} from "../model/gameStatus";
import {Hash} from "../model/hash";
import {Down} from "../model/down";
import {Command, CommandConstructor} from "../commands/Command";
import {IncreaseDownCommand} from "../commands/IncreaseDownCommand";
import {CalculateDistanceCommand} from "../commands/CalculateDistanceCommand";
import {CalculateYardLineCommand} from "../commands/CalculateYardLineCommand";
import {ApplyDownRulesCommand} from "../commands/ApplyDownRulesCommand";
import {Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {fakeGame, fakeGamePlayListGrasset} from "../data/fake-data";
import {LineUpService} from "./lineUp.service";
import {HomeAway} from "../model/home-away";
import {GamePlayService} from "./game-play.service";
import {firstValueFrom, Subject} from "rxjs";
import {environment} from "../../environment/environment";
import { HttpClient } from "@angular/common/http";
import {PlayType} from "../model/playType";
import {exceptionResultList, PlayResult} from "../model/playResult";
import {LoadingService} from "./loading.service";
import {MatDialog} from "@angular/material/dialog";
import {SetHashCommand} from "../commands/SetHashCommand";
import {SetOdkCommand} from "../commands/SetOdkCommand";
import {DialogLineupComponent} from "../components/dialog-lineup/dialog-lineup.component";
import {MatBottomSheet} from "@angular/material/bottom-sheet";
import {captureExceptionWithTags, captureMessageWithTags} from "../../sentry/sentry";
import {AuthService} from "./auth.service";
import {TranslocoService} from "@ngneat/transloco";
import {SafeFirstDownCommand} from "../commands/SafeFirstDownCommand";
import {GroupCommands} from "../commands/GroupCommands";
import {GameType} from "../model/gameType";
import {MessageDialogService} from './message-dialog.service';

@Injectable({
  providedIn: 'root'
})
export class GameService {
  router = inject(Router);
  private _snackBar = inject(MatSnackBar);
  dialog = inject(MatDialog);
  lineUp = inject(LineUpService);
  private gamePlayService = inject(GamePlayService);
  private http = inject(HttpClient);
  private loadingService = inject(LoadingService);
  _bottomSheet = inject(MatBottomSheet);
  authService = inject(AuthService);
  translocoService = inject(TranslocoService);
  private messageDialog = inject(MessageDialogService);

  syncStatus = 'syncked' // syncked, syncking, error
  onLine = true

  worker?: Worker

  private commands: Command[] = [];
  private commandsAfterTagging: Command[] = [];

  private gamePlayUpdateSubject = new Subject<void>();
  gamePlayUpdate$ = this.gamePlayUpdateSubject.asObservable();
  game!: GameInfo


  // On encapsule la liste des GamePlay dans un signal pour bénéficier de la réactivité Angular.
  // Cela permet :
  // - de continuer à exposer la propriété `gamePlayList` sous forme classique (Array<GamePlay>),
  //   donc sans impacter les 100+ fichiers qui l’utilisent déjà.
  // - mais en interne, la valeur est stockée dans un `signal`, ce qui déclenche automatiquement
  //   la mise à jour des `computed` et des composants qui observent la liste.
  // Autrement dit : compatibilité avec l’ancien code + réactivité moderne d’Angular Signals.
  // On utilise un getter/setter classique pour ne pas impacter le code existant et faciliter la migration vers Signals dans l'application.
  // FIXME ajouter incompleteGamePlay
  private _gamePlayList = signal<GamePlay[]>([]);

  get gamePlayList(): GamePlay[] {
    return this._gamePlayList();
  }

  set gamePlayList(value: GamePlay[]) {
    this._gamePlayList.set(value);
  }

  // À utiliser à la place de `this.gamePlayList.push(...)` : push mute le tableau en place,
  // ce qui est invisible pour le signal et pour les computed() qui en dépendent.
  //
  // `getFirstPendingGamePlay()` (lecture) peut, en l'absence de GamePlay pending, en créer un
  // via cette méthode. Comme `getFirstPendingGamePlay()` est appelée directement depuis de
  // nombreux templates (bindings, `@if`), cette écriture peut se produire pendant l'évaluation
  // réactive d'un template, ce qu'Angular Signals interdit (NG0600). `untracked()` détache
  // l'écriture du consumer réactif courant, sans changer le comportement de création.
  public addGamePlay(gamePlay: GamePlay): void {
    untracked(() => this._gamePlayList.update(list => [...list, gamePlay]));
  }

  constructor() {
    if (!this.isUnitTest()) {
      this.worker = new Worker(new URL('../app.worker', import.meta.url))
    }
    this.init()
  }

  init() {
    // pour être overridé par les tests
    this.detectOffline()

    if (!this.isUnitTest()) {
      this.initWorkerListeners();
    }
  }

  async selectGame(gameInfo: GameInfo) {
    this.game = gameInfo

    await this.loadGameData(gameInfo);
    window.localStorage.setItem('gameInfo', JSON.stringify(gameInfo))

    if (this.game.gameStatus === GameStatus.InProgress) {
      if (this.gamePlayList.length === 0) {
        this.afficherKickOff()
        return
      }
      await this.router.navigate(['/resume-game-play']);
    } else if (this.game.gameStatus === GameStatus.InReview || this.game.gameStatus === GameStatus.Completed) {
      await this.router.navigate(['/game-play-list']);
    } else {
      if (this.gamePlayList.length === 0) {
        if (!this.isDemoGame()){
          this.messageDialog.openMessage(
            'validation.warningTitle',
            'validation.lineupValidation',
            'validation.continue',
            'warning'
          ).subscribe(result => {
            if (result?.action === 'confirm') {
              this.openLineupSheet();
            }
          });
        }
        this.afficherKickOff()
        return
      }
    }
  }

  addCommand(commands: Command | Command[]) {
    if (Array.isArray(commands)) {
      this.commands.push(...commands);
    } else {
      this.commands.push(commands);
    }
  }

  addCommandsAfterTagging(commands: Command | Command[]) {
    if (Array.isArray(commands)) {
      this.commandsAfterTagging.push(...commands);
    } else {
      this.commandsAfterTagging.push(commands);
    }
  }

  /**
   * TODO créer tests unitaire
   *
   * Vérifier si une commande est déjà définie.
   *
   *     this.game.hasCommand(SetPenaltyAfterPlayCommand)
   *
   * @param command
   * @param commands
   */
  hasCommand(command: CommandConstructor, commands: Command[] = this.commands): boolean {
    return commands.some(_command => {
      if (_command instanceof GroupCommands) {
        return _command.hasCommand(command)
      }

      return _command instanceof command
    })
  }

  /**
   * Vérifier si une commande "AfterTagging" est déjà définie.
   *
   *     this.game.hasCommandAfterTagging(SetPenaltyAfterPlayCommand)
   *
   * @param command
   */
  hasCommandAfterTagging(command: CommandConstructor): boolean {
    return this.hasCommand(command, this.commandsAfterTagging)
  }

  isGamePending(): boolean {
    return !!this.game
  }

  async reloadGame() {
    const gameInfo = window.localStorage.getItem('gameInfo')
    if (gameInfo) {
      await this.selectGame(JSON.parse(gameInfo))
    }
  }

  public async loadGamePlay(gameInfo: GameInfo) {
    if (this.isDemoGame()) return []
    try {
      const gamePlayList = await firstValueFrom(this.gamePlayService.loadGamePlay(gameInfo.id));
      if (!gamePlayList) {
        throw new Error(`this.gamePlayList est undefined!`)
      }
      return gamePlayList
    } catch (error) {
      console.error('Impossible de charger les game play. Error: ', error);
      captureExceptionWithTags(error, {gameId: gameInfo.id});
      this.messageDialog.openMessage(
        'validation.errorTitle',
        'validation.loadGamePlay',
        'retry',
        'error',
      ).subscribe(result => {
        if (result?.action === 'confirm') {
          this.loadGamePlay(gameInfo); // retry
        }
      });
    }
  }

  openLineupSheet() {
    this._bottomSheet.open(DialogLineupComponent, {
      data: {
        game: this.game,
        homeLineup: this.lineUp.home,
        awayLineup: this.lineUp.away
      },
      panelClass: 'my-bottom-sheet-lineup'
    });
  }

  private async loadGameData(gameInfo: GameInfo) {
    this.loadingService.setLoading(true);
    const [lineUp, gamePlay] = await Promise.all([
      this.loadLineUp(gameInfo),
      this.loadGamePlay(gameInfo)
    ]);
    this.gamePlayList = gamePlay
    this.loadingService.setLoading(false);
  }

  afficherKickOff() {
    this.addGamePlay({
      _status: GameStatus.Pending,
      gamePlayNumber: 1,
      odk: ODK.Kick,
      down: Down.D0,
      distance: 0,
      hash: Hash.M,
      gnLs: 0,
      yardLine: '-45',
      qtr: 1
    })
    this.router.navigate(['/game-play/kickoff-selection']);
  }

  notifyGamePlayUpdate() {
    this.gamePlayUpdateSubject.next();
  }

  public async loadLineUp(gameInfo: GameInfo): Promise<void> {
    if (this.isDemoGame()) {
      this.lineUp.loadLineUpDemo()
      return
    }
    try {
      await firstValueFrom(this.lineUp.loadLineUp(gameInfo.id));
      console.log('Lineup chargé avec succès', gameInfo.id);
    } catch (error) {
      console.error('Impossible de charger les Lineup. Error: ', error);
      captureExceptionWithTags(error, {gameId: gameInfo.id});
      this.messageDialog.openMessage(
        'validation.errorTitle',
        'validation.loadGameLineup',
        'retry',
        'error',
      ).subscribe(result => {
        if (result?.action === 'confirm') {
          this.loadLineUp(gameInfo); // retry
        }
      });
    }
  }

  public async refreshGamePlay() {
    const currentPendingGamePlay = this.getFirstPendingGamePlay();
    const loadedPlays = await this.loadGamePlay(this.game);

    if (!loadedPlays) return;

    this.gamePlayList = loadedPlays; // 👈 passe par le setter → _gamePlayList.set()

    if (this.gamePlayList.length) {
      const lastGamePlay = this.gamePlayList[this.gamePlayList.length - 1];

      if (this.game.gameStatus !== GameStatus.InReview) {
        if (lastGamePlay.gamePlayNumber === currentPendingGamePlay.gamePlayNumber) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          currentPendingGamePlay.gamePlayNumber = lastGamePlay.gamePlayNumber + 1;
        }

        if (exceptionResultList.includes(lastGamePlay.result!)) {
          this.addGamePlay(currentPendingGamePlay);
        } else {
          const nextGamePlay = this.createNextGamePlay(lastGamePlay);
          nextGamePlay.qtr = currentPendingGamePlay.qtr;

          if (!this.isGameKickOff()) {
            this.updateAndValidateDownAndTeams(nextGamePlay, lastGamePlay);
          }
        }
      }
    }
    // Plus besoin de forcer un nouvel array ici : addGamePlay()/le setter passent déjà par le signal.
    this.notifyGamePlayUpdate();
  }


  isGameStarted() {
    return this.gamePlayList.length > 1 && this.game.gameStatus === GameStatus.InProgress;
  }

  addEmptyPlayToGamePlay() {
    const currentPendingGamePlay = this.getFirstPendingGamePlay();
    const lastGamePlay = this.gamePlayList[this.gamePlayList.length - 1];

    const emptyGamePlay = {
      qtr: lastGamePlay.qtr,
    }

    this.http.post<any>(`${environment.apiUrl}/game/${this.game.id}/play-by-play/insert-empty-play-by-play`, {
      emptyGamePlay,
      gamePlayNumber: currentPendingGamePlay.gamePlayNumber
    }).subscribe({
      next: () => {
        this.refreshGamePlay();
        const messageConfirmation = this.translocoService.translate('components.gamePlayEdit.saveConfirmation');
        this._snackBar.open(messageConfirmation, '', {
          horizontalPosition: 'right',
          duration: 3000
        });
      },
      error: error => {
        console.error('Impossible d\'insérer un jeu vide. Error: ', error);
        captureExceptionWithTags(error, {gameId: this.game.id, gamePlayNumber: currentPendingGamePlay.gamePlayNumber});
        this.messageDialog.openMessage(
          'validation.errorTitle',
          'validation.errorUnableToSavePlay',
          'app.ok',
          'error',
        );
      }
    });
  }

  sendGameStats() {
    if (this.isDemoGame()) return
    this.loadingService.setLoading(true);
    return this.http.get(`${environment.apiUrl}/game/${this.game.id}/stats/sendpdf`, {responseType: 'blob' as 'json'})
      .subscribe(
        (response: any) => {
          this.loadingService.setLoading(false);

          this.messageDialog.openMessage(
            'sendStats.title',
            'sendStats.success',
            'app.ok',
            'info'
          ).subscribe(result => {
            if (result?.action === "confirm") {
              this.changeGameStatus(GameStatus.Completed);
              this.router.navigate(['/']);
            }
          });
        },
        error => {
          this.loadingService.setLoading(false);
          captureExceptionWithTags(error, {gameId: this.game.id});
          this.messageDialog.openMessage(
            'sendStats.title',
            'sendStats.error',
            'app.retry',
            'error',
            'app.cancel'
          ).subscribe(result => {
            if (result?.action === 'confirm') {
              this.sendGameStats(); // retry
            }
          });
        }
      );
  }

  private isUnitTest() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Ignorer l'erreur TS2339
    return window.jasmine;
  }

  getGame() {
    return this.game
  }

  isDemoGame() {
    return this.getGame().id === 'Démo'
  }

  setScore({ home, away }: { home?: number, away?: number }) {
    if (home) {
      this.game.homeScore = home
    }
    if (away) {
      this.game.awayScore = away
    }
  }

  updateScore(homeScore: number, awayScore: number)
  {
    if (!this.isDemoGame())
    {
      this.http.post<any>(`${environment.apiUrl}/game/update-score`, {id: this.getGame().id, homeScore, awayScore}).subscribe({
        error: error => {
          // Le score local est déjà mis à jour de façon optimiste ci-dessous : si cet appel
          // échoue silencieusement, le score affiché peut diverger du score réellement
          // enregistré côté serveur sans que personne ne le sache.
          console.error('Impossible de sauvegarder le score sur le serveur. Error: ', error);
          captureExceptionWithTags(error, {gameId: this.getGame()?.id, homeScore, awayScore});
        }
      })
      this.updateLocalScoreGameInfo(homeScore, awayScore)
    }
  }

  getFirstPendingGamePlay(): GamePlay {
    const firstPending = this.gamePlayList.find(gamePlay => gamePlay._status === GameStatus.Pending);
    if (firstPending) {
      return firstPending;
    }

    if (this.gamePlayList.length) {
      const lastGamePlay = this.gamePlayList[this.gamePlayList.length - 1];
      const nextGamePlay = this.createNextGamePlay(lastGamePlay);
      this.updateAndValidateDownAndTeams(nextGamePlay, lastGamePlay);
      return nextGamePlay
    }

    return this.createFirstKOGamePlay()
  }

  createFirstKOGamePlay (): GamePlay {
    const firstGamePlay = {
      _status: GameStatus.Pending,
      gamePlayNumber: 1,
      odk: ODK.Kick,
      down: Down.D0,
      distance: 0,
      hash: Hash.M,
      gnLs: 0,
      yardLine: '-45',
      qtr: 1
    };
    this.addGamePlay(firstGamePlay)
    return firstGamePlay
  }

  changeGameStatus(status: GameStatus) {
    if (this.isDemoGame()) {
      this.game.gameStatus = status
      if (status === GameStatus.InReview) {
        this.router.navigate(['/game-play-list']);
      }
      return
    }
    return this.http.put<any>(`${environment.apiUrl}/game/change-status`, {gameStatus:status,id: this.game.id}).subscribe(
      () => {
        this.game.gameStatus = status
        console.log('Changement de status effectué : ', status);
        if (status === GameStatus.Completed) {
          window.localStorage.removeItem('gameInfo')
          this.loadingService.setLoading(false);
          this.router.navigate(['/']);
          //Forcer la page a refresh pour rafraichir les games. (TODO - Trouver une meilleure solution, sinon on a un écran noir)
          window.location.reload();
        } else if (status === GameStatus.InReview){
          window.localStorage.setItem('gameInfo', JSON.stringify(this.game))
          this.router.navigate(['/game-play-list']);
          this.openStats()
        } else {
          window.localStorage.setItem('gameInfo', JSON.stringify(this.game))
        }
      },
      error => {
        captureExceptionWithTags(error, {gameId: this.game.id});
        console.error('Erreur lors du changement de status : ', error);
      }
    );
  }

  sendHudlStats() {
    if (this.isDemoGame()) return
    this.loadingService.setLoading(true);
    return this.http.get(`${environment.apiUrl}/game/${this.game.id}/stats/sendhudl`, {})
      .subscribe(
        (response: any) => {
          this.messageDialog.openMessage(
            'sendStats.title',
            'sendStats.success',
            'app.ok',
            'info'
          ).subscribe(result => {
            if (result?.action === "confirm") {
              this.changeGameStatus(GameStatus.Completed);
              this.router.navigate(['/']);
            }
          });
        },
        error => {
          this.loadingService.setLoading(false);
          captureExceptionWithTags(error, {gameId: this.game.id});
          this.messageDialog.openMessage(
            'sendStats.title',
            'sendStats.error',
            'app.retry',
            'error',
            'app.cancel'
          ).subscribe(result => {
            if (result?.action === 'confirm') {
              this.sendGameStats(); // retry
            }
          });
        }
      );
  }

  openHudl() {
    if (this.isDemoGame()) return;
    this.loadingService.setLoading(true);

    this.http.get(`${environment.apiUrl}/game/${this.game.id}/stats/generatehudlfile`, {
      responseType: 'blob'
    }).subscribe(
      (response: Blob) => {
        this.loadingService.setLoading(false);

        const url = window.URL.createObjectURL(response);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hudl-${this.game.rseqNumber}.xlsx`; // nom du fichier
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error => {
        this.loadingService.setLoading(false);
        captureExceptionWithTags(error, {gameId: this.game.id});
        this.messageDialog.openMessage(
          'validation.errorTitle',
          'validation.errorDownloadStatsWindow',
          'app.ok',
          'error',
        );
      }
    );
  }


  openStats() {
    if (this.isDemoGame()) return;
    this.loadingService.setLoading(true);
    return this.http.get(`${environment.apiUrl}/game/${this.game.id}/stats/generategamestats`, {responseType: 'text'})
      .subscribe(
        async (response: string) => {
          this.loadingService.setLoading(false);
          const openedWindow = window.open("", "_blank");
          if (!openedWindow) {
            // Gérer le cas où la nouvelle fenêtre ne peut pas être ouverte
            this.loadingService.setLoading(false);
            this.messageDialog.openMessage(
              'validation.errorTitle',
              'validation.errorOpeningStatsWindow',
              'app.ok',
              'error',
            ).subscribe(async result => {
              if (result?.action === 'confirm') {
                const userEmail = await this.authService.getUserEmail();
                this.http.post(`${environment.apiUrl}/game/${this.game.id}/stats/sendstats`, {email: userEmail})
                  .subscribe({
                    next: () => {
                      this.messageDialog.openMessage(
                        'validation.successTitle',
                        'validation.statsEmailed',
                        'app.ok',
                        'info'
                      )
                    },
                    error: error => {
                      captureExceptionWithTags(error, {gameId: this.game.id});
                      this.messageDialog.openMessage(
                        'validation.errorTitle',
                        'validation.errorSendingStatsEmail',
                        'app.ok',
                        'error',
                      )
                    }
                  });
              }
            });
          } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            openedWindow.document.write(response);
          }
        },
        error => {
          this.loadingService.setLoading(false);
          captureExceptionWithTags(error, {gameId: this.game.id});
          this.messageDialog.openMessage(
            'validation.errorTitle',
            'validation.errorDownloadStatsWindow',
            'app.ok',
            'error',
          )
        }
      );
  }

  public requestGameData() {
    const gameId = this.game.id
    if (!gameId) {
      this.messageDialog.openMessage(
        'validation.warningTitle',
        'validation.warningNoGameSelected',
        'validation.continue',
        'warning'
      )
      return
    }
    this.worker?.postMessage({
      // TODO renommer type par action
      type: 'exportGameData',
      gameId: gameId
    });
  }


  isGameKickOff() {
    let isGameKickoff = this.gamePlayList.length <= 1
    if (this.getFirstPendingGamePlay().qtr === 3) {
      const thirdQuarterPlays = this.gamePlayList.filter(game => game.qtr === 3);
      isGameKickoff = thirdQuarterPlays.length <= 1;
    }
    return isGameKickoff;
  }

  isGameInOvertime() {
    return this.getFirstPendingGamePlay().qtr > 4
  }

  isSelectPlayPage() {
    return this.router.url === '/select-play-options'
  }

  getPrecedingGamePlay(gamePlay: GamePlay): GamePlay {
    const index = this.gamePlayList.indexOf(gamePlay)
    const precedingGamePlay = this.gamePlayList[index - 1]
    if (!precedingGamePlay) {
      throw new Error(`Aucun GamePlay précédent`)
    }
    return precedingGamePlay;
  }

  /**
   * Retourne le prochain GamePlay.
   * Et le crée s'il n'existe pas.
   * @param [gamePlay]
   * Si gamePlay est null, le premier gamePlay pending sera utilisé
   * comme gamePlay de référence.
   */
  getNextGamePlay(gamePlay?: GamePlay): GamePlay {
    gamePlay = gamePlay ?? this.getFirstPendingGamePlay()

    const index = this.gamePlayList.indexOf(gamePlay)
    let nextGamePlay = this.gamePlayList[index + 1]
    if (!nextGamePlay) {
      nextGamePlay = this.createNextGamePlay(gamePlay)
    }
    return nextGamePlay;
  }

  /**
   * Création d'un nouveau GamePlay
   */
  public createNextGamePlay(lastGamePlay: GamePlay): GamePlay {
    const nextGamePlay = {
      _status: GameStatus.Pending,
      //TODO -AMELIORER LA LOGIQUE... CAR SI ON A UNE GAME START ET LE DERNIER JEUX EST k, ON EST BLOCK PAR LA SUITE.
      odk: lastGamePlay.odk,
      // FIXME ne fonctionne pas si il y a une erreur dans le POST du worker.
      gamePlayNumber: this.getNextGamePlayNumber(),
      gnLs: 0,
      down: Down.D0,
      hash: lastGamePlay.hash,
      yardLine: '',
      debug: 'createNextGamePlay()',
      qtr: lastGamePlay.qtr,
    };
    this.addGamePlay(nextGamePlay)
    return nextGamePlay;
  }

  getNextGamePlayNumber(): number {
    if (this.gamePlayList.length === 0 ) {
      return 1;
    } else {
      const numbers = this.gamePlayList.map(gp => gp.gamePlayNumber).filter((num): num is number => typeof num === 'number');
      if (numbers.length === 0) {
        return this.gamePlayList.length + 1;
      }
      const maxGamePlayNumber = Math.max(...numbers);
      return maxGamePlayNumber + 1;
    }
  }

  /**
   * - Augmenter le down du gamePlay
   * - Calculer le yardLine
   * - Calculer la distance
   * - Remet à 1/10 si la distance est atteinte
   * - Changer d'équipe si down > 4
   * @param gamePlay
   * @param lastGamePlay
   */
  public updateAndValidateDownAndTeams(gamePlay: GamePlay, lastGamePlay: GamePlay) {
    if (!gamePlay.odk) {
      this.addCommand([
        // Reporter le ODK pour le prochaine gamePlay
        new SetOdkCommand(gamePlay, lastGamePlay.odk)
      ])
    }
    if (!exceptionResultList.includes(lastGamePlay.result!)) {
      this.addCommand([
        new IncreaseDownCommand(gamePlay, lastGamePlay),
        new CalculateDistanceCommand(gamePlay, lastGamePlay),
        new CalculateYardLineCommand(gamePlay, lastGamePlay),
        new ApplyDownRulesCommand(gamePlay, lastGamePlay, this.getNumberOfDowns())
      ]);
    }
    this.addCommand([
      // Reporter le hash pour le prochaine gamePlay
      new SetHashCommand(gamePlay, lastGamePlay.hash)
    ])

    this.addCommandsAfterTagging(new SafeFirstDownCommand(gamePlay));

    this.executeCommands('Exec commands for nextGame tagging...', lastGamePlay)
    this.executeCommandsAfterTagging(lastGamePlay);
  }

  private executeCommandsAfterTagging(currentGamePlay?: GamePlay) {
    console.group('Exec commands after tagging...')
    while (this.commandsAfterTagging.length > 0) {
      const command = this.commandsAfterTagging.shift();
      command?.execute();
    }
    console.groupEnd()

    // FIXME supprimer les valeurs undefined
  }

  hasPendingGamePlay() : boolean {
    const gamesPending = this.gamePlayList.filter(gamePlay => gamePlay._status === GameStatus.Pending);
    return gamesPending.length > 0
  }

  getPendingGamePlays(): GamePlay[] {
    return this.gamePlayList.filter(gamePlay => gamePlay._status === GameStatus.Pending);
  }

  finishTagging(gamePlay: GamePlay) {
    if (this.gamePlayList.length === 1 || this.game.gameStatus === GameStatus.scheduled){
      this.changeGameStatus(GameStatus.InProgress)
    }

    this.executeCommands('', gamePlay);

    gamePlay._status = GameStatus.Completed

    const nextGamePlay = this.getNextGamePlay(gamePlay)

    this.updateAndValidateDownAndTeams(nextGamePlay, gamePlay);

    this.syncBackend(gamePlay)
  }

  saveExistingGamePlay(gamePlay: GamePlay) {
    if (!this.worker) return
    if (this.isDemoGame()) return
    gamePlay._status = GameStatus.Completed
    this.getTokenAndPostUpdateMessage(gamePlay).catch(error => this.handleWorkerDispatchError(error, gamePlay));
  }

  syncBackend(gamePlay: GamePlay) {
    if (!this.worker) return
    if (this.isDemoGame()) return

    gamePlay.gameId = this.game.id
    this.getTokenAndPostMessage(gamePlay).catch(error => this.handleWorkerDispatchError(error, gamePlay));
  }

  /**
   * Si l'obtention du token (ou l'envoi du message au worker) échoue, le gamePlay n'atteint
   * jamais IndexedDB ni le worker : il est perdu silencieusement (aucune stat sauvegardée
   * pour ce jeu, aucune trace nulle part). On le signale donc explicitement à Sentry et à
   * l'utilisateur, plutôt que de laisser la Promise rejeter dans le vide.
   */
  private handleWorkerDispatchError(error: unknown, gamePlay: GamePlay) {
    console.error('Impossible de transmettre le gamePlay au worker pour synchronisation. Error: ', error);
    captureExceptionWithTags(error, {gameId: this.game?.id, gamePlayNumber: gamePlay.gamePlayNumber});
    this.messageDialog.openMessage(
      'validation.errorTitle',
      'validation.errorSyncGamePlay',
      'app.ok',
      'error',
    );
  }

  private async getTokenAndPostMessage(gamePlay: GamePlay): Promise<void> {
    const token = await this.authService.getToken();
    if (this.worker) {
      this.worker.postMessage({
        type: 'save',
        gamePlay,
        authToken: token
      });
    }
  }

  private async getTokenAndPostUpdateMessage(gamePlay: GamePlay): Promise<void> {
    const token = await this.authService.getToken();
    if (this.worker) {
      this.worker.postMessage({
        type: 'updateGamePlay',
        gamePlay,
        authToken: token
      });
    }
  }



  private initWorkerListeners() {
    if (!this.worker) {
      this.messageDialog.openMessage(
        'validation.errorTitle',
        'validation.errorNoWorker',
        'retry',
        'error',
      ).subscribe(result => {
      });
      return
    }
    this.worker.onmessage = async ({ data }) => {
      console.log(`Worker : Réception d'un message du worker`, data)

      if (data.type === 'save') {
        switch (data.status) {
          case 'saving':
            this.syncStatus = 'syncking';
            break;
          case 'error':
            this.syncStatus = 'error';
            break;
          case 'done':
            this.syncStatus = 'syncked';
            this.updateGamePlay(data.savedGamePlay);
            break;
          case 'permanentError':
            // Le worker a définitivement abandonné ce gamePlay (erreur non-réseau,
            // non-401 : payload invalide, quota IndexedDB dépassé, etc.) et ne le
            // retentera plus jamais automatiquement. On le signale à Sentry avec le
            // contexte, sinon cette perte de données passe totalement inaperçue.
            this.syncStatus = 'error';
            captureMessageWithTags(
              `Le worker a abandonné la synchronisation du gamePlay #${data.gamePlayNumber} après une erreur permanente : ${data.error?.message}`,
              {gameId: this.game?.id, gamePlayNumber: data.gamePlayNumber, gamePlayUuid: data.gamePlayUuid},
              {key: 'worker.permanentError', data: {error: data.error}}
            );
            break;
        }
        return;
      }

      // ⚡ Nouveau cas : le worker a besoin d'un token frais
      if (data.type === 'refreshTokenNeeded') {
        console.warn('Worker demande un refresh de token');
        const newToken = await this.authService.getToken(true); // force refresh
        if (this.worker) {
          this.worker.postMessage({
            type: 'updateToken',
            authToken: newToken
          });
        }
      }

      if (data.type === 'exportGameDataResponse') {
        this.downloadGameData(data.exportedGameData)
        return
      }

      console.warn('Message inconnu', data)
    };

    console.log('Worker : Listeners installés')
  }

  downloadGameData(gameData: any) {
    const jsonData = JSON.stringify(gameData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `gameData.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  updateGamePlay(savedGamePlay: GamePlay) {
    console.log(`Update du gamePlay id : `, savedGamePlay.id)
    const originalGamePlay = this.gamePlayList
      .find(gp => gp.gamePlayNumber === savedGamePlay.gamePlayNumber)

    if (originalGamePlay) {
      originalGamePlay.id = savedGamePlay.id
    } else {
      captureMessageWithTags(
        `Erreur de mise à jour du GamePlay après la sauvegarde du Worker! Impossible de trouver le gameplay original. `,
        {gameId: this.game?.id, gamePlayNumber: savedGamePlay.gamePlayNumber},
        {key: 'updateGamePlay', data: {savedGamePlay, gamePlayNumberList: this.gamePlayList.map(gp => gp.gamePlayNumber)}}
      )
    }
  }

  detectOffline() {
    const handleNetworkChange = () => this.onLine = navigator.onLine
    window.addEventListener("online", handleNetworkChange);
    window.addEventListener("offline", handleNetworkChange);
  }

  private executeCommands(comment?: string, currentGamePlay?: GamePlay) {
    console.group(comment ?? 'Exec commands...')
    while (this.commands.length > 0) {
      const command = this.commands.shift();
      command?.execute();
    }
    console.groupEnd()
  }

  /**
   * Retire une commande spécifique de la liste des commandes pour s'assurer qu'elle ne sera pas exécutée.
   * Cette méthode permet de retirer une commande basée sur son type et une propriété spécifique.
   *
   * @template T
   *    Le type de la commande à retirer.
   *    Exemple: `SetPlayResultCommand`.
   *
   * @template K
   *    La clé de la propriété de la commande T utilisée pour la comparaison.
   *
   * @param {new(...args: any[]) => T} type
   *    Le constructeur de la commande à retirer.
   *
   * @param {K} property
   *    La propriété de la commande à comparer. Elle doit être une propriété de la commande et être publique.
   *    Une commande peut être présente plusieurs fois dans la liste, donc il est essentiel de retirer la bonne instance.
   *    Exemple: `'result'`.
   *
   * @param {T[K]} value
   *    La valeur de la propriété à comparer.
   *
   * @example
   *     this.game.removeCommand(SetPlayResultCommand, 'result', PlayResult.Penalty);
   *     this.game.removeCommand(SetSimpleCommand, 'result', PlayResult.Simple);
   */
  removeCommand<T, K extends keyof T>(type: { new(...args: any[]): T; }, property: K, value: any): void {
    const commandIndex = this.commands.findIndex(
      cmd => cmd instanceof type && cmd[property] === value
    );

    if (commandIndex !== -1) {
      this.commands.splice(commandIndex, 1);
    }
  }

  //TODO - Remplacer les if ODK pour getPossession
  getPossession(gamePlay: GamePlay) {
    switch (gamePlay.odk) {
      case ODK.Kick:
        switch (gamePlay.playType) {
          case PlayType.KO:
          case PlayType.Punt:
          case PlayType.FG:
          case PlayType.ExtraPt:
            return HomeAway.Home
          case PlayType.KOREC:
          case PlayType.PuntRec:
          case PlayType.FGBlock:
          case PlayType.ExtraPtBlock:
            return HomeAway.Away
          case PlayType.OnsideKick:
          case PlayType.OnsideKickRec:
            if (gamePlay.result === PlayResult.OnsideKickHomeTD) {
              return HomeAway.Home
            } else {
              return HomeAway.Away
            }
          default:
            if (this.gamePlayList.length <= 1 ) {
              return HomeAway.Home
            }
            //On doit remettre la possession à l'équipe qui a fait le dernier jeu puisque nous avons un choix de jeu sur la page de sélection
            if (this.getPrecedingGamePlay(gamePlay)?.playType === PlayType.FG) {
              return HomeAway.Home
            }
            if (this.getPrecedingGamePlay(gamePlay)?.playType === PlayType.FGBlock) {
              return HomeAway.Away
            }
            return HomeAway.Home
        }
      case ODK.Offensive:
        return HomeAway.Home
      case ODK.Defensive:
        return HomeAway.Away
      default:
        return HomeAway.Home
    }
  }

  getCorrectODKPossession() {
    return this.getPossession(this.getFirstPendingGamePlay()) === HomeAway.Home ? ODK.Offensive : ODK.Defensive
  }

  // Returns true if home team has ball possession. Ignoring game kickoff.
  // No team selected if game kickoff.
  homeHasPossession() {
    return this.getPossession(this.getFirstPendingGamePlay()) === HomeAway.Home
      && !this.isGameKickOff() && !this.isSelectPlayPage()
  }

  // Returns true if away team has ball possession.
  // No team selected if game kickoff.
  awayHasPossession() {
    return this.getPossession(this.getFirstPendingGamePlay()) === HomeAway.Away
      && !this.isGameKickOff() && !this.isSelectPlayPage()
  }

  isTeamHome(teamId:string){
    return this.game.homeTeam.id === teamId
  }

  getNumberOfDowns(): number {
    if (!this.game) {
      throw new Error("GameInfo not set.")
    }

    return this.game.numberOfDowns
  }

  getOffensiveLineUp() {
    const possession = this.getPossession(this.getFirstPendingGamePlay())
    return this.lineUp[possession]
  }

  getDefensiveLineUp() {
    const possession = this.getPossession(this.getFirstPendingGamePlay())
    const otherPossession = possession === 'home' ? 'away' : 'home'
    return this.lineUp[otherPossession]
  }

  getKickerLineUp() {
    const gamePlay = this.getFirstPendingGamePlay()
    let possession = HomeAway.Home
    switch (gamePlay.playType) {
        case PlayType.KOREC:
        case PlayType.PuntRec:
        case PlayType.FGBlock:
        case PlayType.OnsideKickRec:
        case PlayType.ExtraPtBlock:
          possession = HomeAway.Away
            break
        case PlayType.Punt:
        case PlayType.KO:
        case PlayType.OnsideKick:
        case PlayType.ExtraPt:
        case PlayType.FG:
          possession = HomeAway.Home
            break
        default:
          possession = HomeAway.Home
          break
      }
    return this.lineUp[possession]
  }

  getReturnerLineUp() {
    const gamePlay = this.getFirstPendingGamePlay()
    let possession = HomeAway.Home
    switch (gamePlay.playType) {
      case PlayType.KOREC:
      case PlayType.PuntRec:
      case PlayType.FGBlock:
      case PlayType.ExtraPtBlock:
        possession = HomeAway.Home
        break
      case PlayType.Punt:
      case PlayType.KO:
      case PlayType.ExtraPt:
      case PlayType.FG:
        possession = HomeAway.Away
        break
      case PlayType.OnsideKickRec:
        if (gamePlay.result === PlayResult.Recovered) {
          possession = HomeAway.Away
        }else {
          possession = HomeAway.Home
        }
        break
      case PlayType.OnsideKick:
        if (gamePlay.result === PlayResult.Recovered) {
          possession = HomeAway.Home
        }else {
          possession = HomeAway.Away
        }
        break
      default:
        possession = HomeAway.Home
        break
    }
    return this.lineUp[possession]
  }

  getForceFumblePlayers(recoveredByTeam: HomeAway) {
    if (this.getFirstPendingGamePlay().odk === ODK.Offensive){
      return this.lineUp[HomeAway.Away].defensive
    } else if (this.getFirstPendingGamePlay().odk === ODK.Defensive) {
      return this.lineUp[HomeAway.Home].defensive
    } else {
      if (recoveredByTeam === HomeAway.Home){
        return this.lineUp[HomeAway.Home].fullTeam
          .filter(player => player.gamesPlayed[0])
          .map(player => player.gamesPlayed[0].gameJerseyNumber)
      } else {
        return this.lineUp[HomeAway.Away].fullTeam
          .filter(player => player.gamesPlayed[0])
          .map(player => player.gamesPlayed[0].gameJerseyNumber)
      }
    }
  }

  getFumbleRecoveryPlayer(teamRecovered: HomeAway) {
    if (this.getFirstPendingGamePlay().odk === ODK.Offensive){
      if (teamRecovered === HomeAway.Home) {
        return this.lineUp[teamRecovered].offensive
      } else {
        return this.lineUp[teamRecovered].defensive
      }
    } else if (this.getFirstPendingGamePlay().odk === ODK.Defensive) {
      if (teamRecovered === HomeAway.Home){
        return this.lineUp[teamRecovered].defensive
      } else {
        return this.lineUp[teamRecovered].offensive
      }
    } else{
      if (teamRecovered === HomeAway.Home){
        return this.lineUp[HomeAway.Home].fullTeam
          .filter(player => player.gamesPlayed[0])
          .map(player => player.gamesPlayed[0].gameJerseyNumber)
      } else {
        return this.lineUp[HomeAway.Away].fullTeam
          .filter(player => player.gamesPlayed[0])
          .map(player => player.gamesPlayed[0].gameJerseyNumber)
      }
    }
  }

  dump() {
    console.table(this.gamePlayList)
    window.localStorage.setItem('dump', JSON.stringify(this.gamePlayList))
  }

  debug() {
    // eslint-disable-next-line no-debugger
    debugger
  }

  getCsv() {
    const csv = [
      [
        'down',
        'distance',
        'playType',
        'result',
        'gnLs',
        'odk',
        'yardLine',
        'hash',

        'kickYards',
        'returnYards',

        '_status'
      ].join(',')
    ]
    this.gamePlayList.forEach(gamePlay => {
      csv.push([
        gamePlay.down,
        gamePlay.distance,
        gamePlay.playType ?? '',
        gamePlay.result ?? '',
        gamePlay.gnLs,
        gamePlay.odk,
        gamePlay.yardLine,
        gamePlay.hash,

        gamePlay.kickYards ?? '',
        gamePlay.returnYards ?? '',

        gamePlay._status,
      ].join(','))
    })
    return csv // .join('\n')
  }

  // TODO à extraire
  initFakeData() {
    this.game = fakeGame
    this.gamePlayList = fakeGamePlayListGrasset
    this.router.navigate(['/game-play']);
  }

  updateLocalScoreGameInfo(homeScore: number, awayScore: number) {
    // Mettre à jour le localStorage
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - On sait que gameData est un objet JSON
    const gameData = JSON.parse(localStorage.getItem('gameInfo'));
    if (gameData) {
      gameData.homeScore = Number(homeScore);
      gameData.awayScore = Number(awayScore);

      // Sauvegarder l'objet mis à jour dans le localStorage
      localStorage.setItem('gameInfo', JSON.stringify(gameData));
    }
  }

  isExhibitionGame = () => {
    return this.game.gameType === GameType.Exhibition
  }
}
